import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import classNames from "classnames";
import { useIntl } from "react-intl";
import { useAtom, useAtomValue } from "jotai";
import { LabelKeyValueItem, Skill } from "@/model";
import skillRepository from "@/repository/skillRepository";
import { useSkills } from "@/query/skill";
import { useIntents } from '@/query/intent';
import { singleTestPanelOpenAtom } from "@/atom/singleTestPanel";
import { intentSearchKeywordAtom, intentSearchTypeAtom } from "@/atom/intentTable";
import { useAgentId, useModal, useRouteHandler } from "@/hook";
import Button from "@/component/Button";
import { TypeSearchbar } from '@/component/input';
import { UploadModal } from "@/component/modal";
import { SingleTestPanel, SingleTestButton } from '@/component/singleTest';
import { AddIcon, UploadIcon, DownloadIcon } from '@/assets/svgs/icons';

import SkillTable from "./component/skill/SkillTable";
import SkillModifyModal from "./component/skill/SkillModifyModal";
import IntentSettingModal from "./component/intent/IntentSettingModal";
import SentenceAnswerEditModal from "./component/SentenceAnswerEditModal";
import IntentSlotModal from "./component/intent/IntentSlotModal";
import IntentTransferModal from "./component/intent/IntentsTransferModal";
import IntentTable, { INTENT_UPLOAD_MODAL_ID } from "./component/intent/IntentTable";
import SkillAddModal, { SKILL_ADD_MODAL_ID } from "./component/skill/SkillAddModal";
import IntentAddModal, { INTENT_ADD_MODAL_ID } from "./component/intent/IntentAddModal";


const SEARCH_TYPE_OPTIONS: LabelKeyValueItem[] = [
    { labelKey: "i03069", value: "intent" },
    { labelKey: "i03070", value: "intentSentence" }
];

const IntentView: React.FC = () => {
    const intl = useIntl();
    const location = useLocation();
    const agentId = useAgentId();
    const { removeStateKey } = useRouteHandler();
    const modal = useModal();

    const [selectedSkill, setSelectedSkill] = useState<Skill>();

    const [searchKeyword, setSearchKeyword] = useAtom(intentSearchKeywordAtom);
    const [searchType, setSearchType] = useAtom(intentSearchTypeAtom);
    const singleTestPanelOpen = useAtomValue(singleTestPanelOpenAtom);
    const { data: skills } = useSkills(agentId, searchKeyword, searchType);
    const { data: intents } = useIntents(agentId, selectedSkill?.id);

    const onSearch = (keyword: string, option: LabelKeyValueItem) => {
        setSearchKeyword(keyword);
        setSearchType(option.value);
    };

    useEffect(() => {
        if (!skills?.length) {
            return;
        }
        if (location.state?.skillId) {
            setSelectedSkill(skills.find(skill => (
                skill.id === location.state.skillId
            )));
            removeStateKey('skillId');
        } else if (!selectedSkill || !skills.find(skill => (
            skill.id === selectedSkill?.id
        ))) {
            setSelectedSkill(skills[0]);
        }
        // eslint-disable-next-line
    }, [skills, location, removeStateKey, setSelectedSkill]);

    useEffect(() => {
        if (!selectedSkill) {
            return;
        }
        setSelectedSkill(skills?.find(skill => skill.id === selectedSkill.id));
        // eslint-disable-next-line
    }, [skills]);

    useEffect(() => {
        return () => {
            setSearchKeyword(undefined);
            setSearchType("intent");
        };
    }, [setSearchKeyword, setSearchType]);

    return (<>
        <SkillAddModal />
        <SkillModifyModal />
        <IntentAddModal />
        <IntentSettingModal />
        <IntentSlotModal />
        <SentenceAnswerEditModal />
        <IntentTransferModal skillId={selectedSkill?.id} />
        <UploadModal id={INTENT_UPLOAD_MODAL_ID}
                     label={intl.formatMessage({ id: "i03002" })}
                     cleanImportText={intl.formatMessage({ id: "i03071" })}
                     sampleFileName="nlu_intent_training_sample"
                     sampleFileColumns={[
                         { attribute: "query", description: intl.formatMessage({ id: "i03003" }) },
                         { attribute: "intent_name", description: intl.formatMessage({ id: "i03004" }) },
                         { attribute: "skill_name", description: intl.formatMessage({ id: "i03005" }) }
                     ]}
                     upload={async (file, isCleanImport) => await skillRepository.import(agentId, file, isCleanImport)}
        />

        <div className={classNames(
                 "agent-content-layout",
                 singleTestPanelOpen && "test-panel-open"
             )}
        >
            <div className={classNames(
                      "full-page-container flex flex-col",
                      singleTestPanelOpen && "test-panel-open"
                  )}
            >
                <div className="search-bar-container mb-4.5">
                    <TypeSearchbar width={680}
                                   searchTypes={SEARCH_TYPE_OPTIONS}
                                   onSearch={onSearch}
                    />
                </div>

                <div className="table-btn-container mb-2.5">
                    <div className="min-w-left-editor-section flex items-center">
                        <Button variant="text"
                                onClick={() => modal.open(SKILL_ADD_MODAL_ID)}
                        >
                            <AddIcon className="size-3.5" />
                            {intl.formatMessage({ id: "i03009" })}
                        </Button>
                    </div>
                    <div className={classNames(
                             "w-full flex items-center justify-between",
                             singleTestPanelOpen && "pr-5"
                         )}
                    >
                        <Button variant="text"
                                disabled={!selectedSkill}
                                onClick={() => modal.open(INTENT_ADD_MODAL_ID, selectedSkill!)}
                        >
                            <AddIcon className="size-3.5" />
                            {intl.formatMessage({ id: "i03021" })}
                        </Button>
                        
                        <div className="flex items-end gap-1">
                            <Button variant="text"
                                    onClick={() => modal.open(INTENT_UPLOAD_MODAL_ID)}
                            >
                                <UploadIcon className="size-3.5" />
                                {intl.formatMessage({ id: "b0004" })}
                            </Button>

                            <Button variant="text"
                                    onClick={() => skillRepository.export(agentId)}
                            >
                                <DownloadIcon className="size-3.5" />
                                {intl.formatMessage({ id: "b0005" })}
                            </Button>

                            <SingleTestButton />
                        </div>
                    </div>
                </div>

                <div className="form-table-container h-full">
                    <div className="table-pagination-container overflow-y-auto">
                        <SkillTable skills={skills ?? []}
                                    selectedSkill={selectedSkill}
                                    onClick={setSelectedSkill}
                                    onDelete={setSelectedSkill}
                        />

                        <div className="table-count-info">
                            {intl.formatMessage({ id: "i00024" }, {
                                total: skills?.length ?? 0,
                                current: skills?.length ?? 0
                            })}
                        </div>
                    </div>
                    <div className="table-pagination-container">
                        <IntentTable intents={intents}
                                     skill={selectedSkill}
                                     searchType={searchType}
                                     searchKeyword={searchKeyword}
                        />

                        <div className="table-count-info">
                            {intl.formatMessage({ id: "i00024" }, {
                                total: intents?.length ?? 0,
                                current: intents?.length ?? 0
                            })}
                        </div>
                    </div>
    
                    <SingleTestPanel className="ml-auto" />
                </div>
            </div>
        </div>
    </>);
};

export default IntentView;
